import * as Sentry from "@sentry/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  BadgeCheckIcon,
  CameraIcon,
  CameraOff,
  Loader2Icon,
  OctagonAlertIcon,
  RotateCcwIcon,
  UploadCloudIcon,
  VideoIcon,
} from "lucide-react"
import { MutableRefObject, useEffect, useRef, useState } from "react"
import RecordRTC, { MediaStreamRecorder } from "recordrtc"
import {
  getVideoVerificationStatus,
  uploadVideoFile,
  VerificationStatusMap,
} from "@/api/api"
import { NextStep } from "@/components/next-step"
import { PageLoader } from "@/components/page-loader"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { useCountdown } from "@/lib/use-countdown"
import { useCurrentToken } from "@/lib/use-current-token"
import { cn, formatSeconds, parseError } from "@/lib/utils"

enum VideoRecorderStatus {
  IDLE = "idle",
  PERMISSIONS_REQUESTED = "permissions_request",
  PERMISSIONS_DENIED = "permissions_denied",
  PERMISSIONS_GRANTED = "permissions_granted",
  RECORDING = "recording",
  STOPPED = "stopped",
  PREVIEW = "preview",
}

type TRecorder = ReturnType<typeof useVideoRecorder>
type TUploader = ReturnType<typeof useVideoUploader>

type RecorderControlsProps = {
  recorder: TRecorder
  uploader: TUploader
}

const useVideoRecorder = (
  videoRef: MutableRefObject<HTMLVideoElement | null>,
) => {
  const [recordedFile, setRecordedFile] = useState<File>()
  const [error, setError] = useState<string>()
  const [state, setState] = useState<VideoRecorderStatus>(
    VideoRecorderStatus.IDLE,
  )

  const cameraRef = useRef<MediaStream>()
  const recorderRef = useRef<RecordRTC>()

  const isIdle = state === VideoRecorderStatus.IDLE
  const isRecording = state === VideoRecorderStatus.RECORDING
  const isStopped = state === VideoRecorderStatus.STOPPED
  const isPermissionsDenied = state === VideoRecorderStatus.PERMISSIONS_DENIED
  const isPermissionsGranted = state === VideoRecorderStatus.PERMISSIONS_GRANTED
  const isPermissionsRequested =
    state === VideoRecorderStatus.PERMISSIONS_REQUESTED

  useEffect(() => {
    if (isPermissionsRequested) {
      setRecordedFile(undefined)

      navigator.mediaDevices
        .getUserMedia({
          audio: true,
          video: true,
        })
        .then((stream) => {
          cameraRef.current = stream
          setState(VideoRecorderStatus.PERMISSIONS_GRANTED)

          const videoTracks = stream.getVideoTracks()
          const audioTracks = stream.getAudioTracks()
          console.log(
            `Got ${videoTracks.length} video tracks from media stream`,
          )

          console.log(
            `Got ${audioTracks.length} audio tracks from media stream`,
          )

          for (const vt of videoTracks) {
            console.log({
              kind: vt.kind,
              label: vt.label,
              enabled: vt.enabled,
              readyState: vt.readyState,
              muted: vt.muted,
            })
          }

          for (const at of audioTracks) {
            console.log({
              kind: at.kind,
              label: at.label,
              enabled: at.enabled,
              readyState: at.readyState,
              muted: at.muted,
            })
          }
        })
        .catch((e) => {
          // TODO: catch device not supported error
          console.error("Failed to get access to media devices", e)
          setState(VideoRecorderStatus.PERMISSIONS_DENIED)
          setError("Permisos de cámara y micrófono fueron denegados")
        })
    }
  }, [state, setState, setError, cameraRef, isPermissionsRequested])

  useEffect(() => {
    if (isPermissionsGranted && cameraRef.current && videoRef.current) {
      videoRef.current.src = ""
      videoRef.current.autoplay = true
      videoRef.current.controls = false
      videoRef.current.playsInline = true
      videoRef.current.muted = true
      videoRef.current.srcObject = cameraRef.current

      recorderRef.current = new RecordRTC(cameraRef.current, {
        type: "video",
        mimeType: "video/mp4",
        timeSlice: 1000,
        recorderType: MediaStreamRecorder,
      })

      recorderRef.current.reset()
    }
  }, [cameraRef, videoRef, state, recorderRef, isPermissionsGranted])

  useEffect(() => {
    if (isStopped && recorderRef.current && videoRef.current) {
      videoRef.current.src = ""
      videoRef.current.srcObject = null
      videoRef.current.autoplay = false
      videoRef.current.controls = true
      videoRef.current.muted = false
      videoRef.current.volume = 1

      const blob = recorderRef.current.getBlob()
      if (!blob) {
        setError("Hubo un error al procesar el video")
        return
      }

      if (blob.size === 0) {
        setError(
          "Hubo un error al procesar el video. Por favor intente con otro navegador",
        )
        Sentry.captureMessage(
          "Couldn't capture video, recorded File is 0 bytes",
          "error",
        )
        return
      }

      videoRef.current.src = URL.createObjectURL(blob)
      setRecordedFile(
        new File([blob], "confirmation-video", {
          type: blob.type,
        }),
      )

      recorderRef.current.destroy()

      // Stop camera streaming
      cameraRef.current?.getTracks().forEach((track) => track.stop())
    }
  }, [cameraRef, videoRef, recorderRef, state, setRecordedFile, isStopped])

  const [recordingDuration, { startCountdown, stopCountdown, resetCountdown }] =
    useCountdown({
      countStart: 0,
      countStop: Infinity,
      intervalMs: 1000,
      isIncrement: true,
    })

  return {
    error,
    recordingDuration,
    recordedFile,
    isIdle,
    isRecording,
    isStopped,
    isPermissionsRequested,
    isPermissionsDenied,

    requestPermissions: () => {
      setState(VideoRecorderStatus.PERMISSIONS_REQUESTED)
      resetCountdown()
    },

    record: () => {
      recorderRef.current?.startRecording()
      setState(VideoRecorderStatus.RECORDING)
      startCountdown()
    },

    stop: () => {
      recorderRef.current?.stopRecording(() => {
        setState(VideoRecorderStatus.STOPPED)
        stopCountdown()
      })
    },
  }
}

const useVideoUploader = (token_id: string) => {
  const [uploadProgress, onUploadProgress] = useState(0)
  const queryClient = useQueryClient()

  const uploadMutation = useMutation({
    mutationKey: ["uploadVideo", { token_id }],
    mutationFn: uploadVideoFile,
    onSuccess: () =>
      queryClient.refetchQueries({
        queryKey: ["getVideoStatus", { token_id }],
      }),
  })

  return {
    uploadProgress,
    error: uploadMutation.error,
    isPending: uploadMutation.isPending,
    isSuccess: uploadMutation.isSuccess,
    isError: uploadMutation.isError,
    mutate: (file: File) =>
      uploadMutation.mutate({ file, token_id, onUploadProgress }),
  }
}

const RecorderControls = ({ recorder, uploader }: RecorderControlsProps) => {
  if (recorder.isStopped) {
    return (
      <div className={cn("flex flex-col gap-4 md:flex-row", "justify-between")}>
        <Button
          variant="outline"
          size="lg"
          disabled={uploader.isPending}
          onClick={() => recorder.requestPermissions()}
        >
          <RotateCcwIcon className="mr-2" size={18} />
          <span>Grabar de nuevo</span>
        </Button>

        <Button
          className="shadow"
          variant="default"
          size="lg"
          disabled={uploader.isPending || !recorder.recordedFile}
          onClick={() => {
            if (recorder.recordedFile) {
              uploader.mutate(recorder.recordedFile)
            }
          }}
        >
          <UploadCloudIcon className="mr-2" size={18} />
          <span>Subir video de confirmación</span>
        </Button>
      </div>
    )
  }

  if (recorder.isRecording) {
    return (
      <Button
        variant="outline"
        size="lg"
        className={"w-full shadow-inner md:w-auto"}
        onClick={() => recorder.stop()}
      >
        <span className="mr-2 h-5 w-5 rounded bg-rose-500"></span>
        <span>Detener Grabación</span>
      </Button>
    )
  }

  return (
    <Button
      variant="outline"
      size="lg"
      className={"w-full shadow md:w-auto"}
      onClick={() => recorder.record()}
    >
      <span className="mr-2 h-5 w-5 rounded-full bg-rose-500"></span>
      <span>Iniciar Grabación</span>
    </Button>
  )
}

const UploaderStatus = ({ uploader }: { uploader: TUploader }) => {
  if (uploader.isPending) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-md bg-secondary p-6">
        <Progress value={uploader.uploadProgress} className="h-6 md:w-96" />
        <div className="text-center">
          ¡Subiendo video, no cierre ésta página!
        </div>
      </div>
    )
  }

  if (uploader.isSuccess) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md bg-secondary p-6">
        <BadgeCheckIcon size={48} className="text-primary" />
        <div>¡Video subido correctamente!</div>
      </div>
    )
  }

  if (uploader.isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md bg-secondary p-6">
        <OctagonAlertIcon size={48} className="text-destructive" />
        <div>¡Ha ocurrido un error inesperado!</div>
        <div>{uploader.error && <div>{parseError(uploader.error)}</div>}</div>
      </div>
    )
  }

  return null
}

const VideoRecordingStatus = ({ recorder }: { recorder: TRecorder }) => {
  if (recorder.isStopped) {
    return null
  }

  return (
    <div className="absolute flex w-full justify-end">
      <div className="mr-4 mt-2 flex items-center gap-2 rounded-md bg-black/30 px-4 py-2 font-mono text-white transition transition-all">
        {recorder.isRecording && (
          <>
            <div className="relative">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-500/75"></span>
              <span className="block h-5 w-5 rounded-full bg-rose-500"></span>
            </div>

            <span>Grabando ·</span>
          </>
        )}

        <span>{formatSeconds(recorder.recordingDuration)}</span>
      </div>
    </div>
  )
}

const VideoRecorder = ({ token_id }: { token_id: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const recorder = useVideoRecorder(videoRef)
  const uploader = useVideoUploader(token_id)

  if (recorder.isIdle || recorder.isPermissionsRequested) {
    return (
      <Button
        variant="outline"
        onClick={() => recorder.requestPermissions()}
        asChild
      >
        <div
          className={cn(
            "flex flex-col items-center justify-center gap-4",
            "h-96 w-full rounded-md border bg-secondary",
            "cursor-pointer text-wrap p-4 text-center",
            "hover:bg-secondary/80",
          )}
        >
          {recorder.isPermissionsRequested ? (
            <Loader2Icon className="animate-spin" size={48} />
          ) : (
            <>
              <CameraIcon size={48} className="text-primary" />
              <span className="text-lg md:text-2xl">
                Haz click aquí para activar tu cámara
              </span>
            </>
          )}
        </div>
      </Button>
    )
  }

  if (recorder.isPermissionsDenied) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-4",
          "h-96 w-full rounded-md border bg-secondary",
          "p-4 text-center",
        )}
      >
        <CameraOff size={48} className="text-destructive" />
        {recorder.error}
      </div>
    )
  }

  if (recorder.error) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-4",
          "h-96 w-full rounded-md border bg-secondary",
          "p-4 text-center",
        )}
      >
        <OctagonAlertIcon className="text-destructive" size={48} />
        {recorder.error}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <VideoRecordingStatus recorder={recorder} />

        <video
          ref={videoRef}
          width="100%"
          muted
          autoPlay
          playsInline
          className="rounded-md"
        />
      </div>

      <UploaderStatus uploader={uploader} />

      {!uploader.isPending && (
        <RecorderControls recorder={recorder} uploader={uploader} />
      )}
    </div>
  )
}

const VideoPage = () => {
  const { token_id, isReady } = useCurrentToken()

  const statusQuery = useQuery({
    queryKey: ["getVideoStatus", { token_id }],
    queryFn: () => getVideoVerificationStatus({ token_id }),
    enabled: isReady,
    refetchOnWindowFocus: false,
  })

  if (!isReady || statusQuery.isLoading) {
    return <PageLoader />
  }

  if (statusQuery.isError) {
    throw statusQuery.error
  }

  if (!statusQuery.isSuccess || !statusQuery.data) {
    throw new Error("Error inesperado")
  }

  const currentStatus = statusQuery.data

  const isVerified =
    (currentStatus.status !== "unverified" && !!currentStatus.video_url) ||
    currentStatus.status === VerificationStatusMap.VIDEO_VERIFIED

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12">
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col items-center justify-center md:flex-row">
            <VideoIcon className="md:mr-3" />
            <h1 className="text-center text-xl md:text-2xl">
              Verificación por video
            </h1>
          </div>
        </CardHeader>

        <CardContent>
          {isVerified ? (
            <div className="flex flex-col items-center">
              <BadgeCheckIcon size={64} className="mb-6 text-primary" />

              <p className="pb-6 text-center">
                ¡Tu video de confirmación ha sido verificado exitosamente!
              </p>

              <NextStep />
            </div>
          ) : (
            <>
              <p className="text-center">
                Graba un video leyendo el siguiente texto en voz alta
              </p>

              <blockquote className="my-6 rounded-md bg-secondary/50 p-3 text-justify">
                {currentStatus.legend}
              </blockquote>

              <VideoRecorder token_id={token_id} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export { VideoPage }
